import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Phone, ZoomIn, ZoomOut } from 'lucide-react';
import { APIProvider, Map, AdvancedMarker, Pin, useMap } from '@vis.gl/react-google-maps';
import TrackIcon from '../assets/svg/Track.svg';
import men from '../assets/images/men.png';
import Buttons from '../assets/images/Buttons.png';

// Helper: get route param token from URL (e.g., /ride-tracking/:token)
const getTokenFromUrl = () => {
  // Try pathname first
  let match = window.location.pathname.match(/ride-tracking\/([^/?]+)/i);
  if (match) {
    return decodeURIComponent(match[1]);
  }
  // Fallback to query parameter
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  return token ? decodeURIComponent(token) : '';
};

// Helper: get API base URL - always use direct AWS API URL
const getApiBaseUrl = () => {
  return 'https://api.egtogo.com';
};

const RideTrackingScreen = () => {
  const [pickupLocation, setPickupLocation] = useState(null);
  const [dropLocation, setDropLocation] = useState(null);
  const [pickupAddress, setPickupAddress] = useState('');
  const [dropAddress, setDropAddress] = useState('');
  const [vehiclePosition, setVehiclePosition] = useState(null);
  const [driverId, setDriverId] = useState('');
  const [driverPhone, setDriverPhone] = useState('');
  const [driverName, setDriverName] = useState('');
  const [rideId, setRideId] = useState('');
  const [rideName, setRideName] = useState('');
  const [trackingEnded, setTrackingEnded] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [directions, setDirections] = useState(null);
  const [routePath, setRoutePath] = useState([]); // For Polyline fallback
  // eslint-disable-next-line no-unused-vars
  const [zoom, setZoom] = useState(14);
  const [estimatedTime, setEstimatedTime] = useState(null);

  const mapRef = useRef(null);
  const directionsServiceRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const polylineRef = useRef(null); // For manual polyline rendering

  const mapContainerStyle = {
    width: '100%',
    height: '100%'
  };

  // Helper to fetch address from lat/lng using Google Geocoding or backend
  const fetchAddress = async (location) => {
    if (!location) return '';
    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(
        `${apiBaseUrl}/search-location?latlng=${location.lat},${location.lng}`,
        { method: 'GET', mode: 'cors', headers: { 'Content-Type': 'application/json' } }
      );
      if (response.ok) {
        const data = await response.json();
        return data.results?.[0]?.formatted_address || '';
      }
    } catch (e) {
      // CORS or network error - fallback to Google Geocoding
      console.warn('Backend geocoding failed, using Google fallback:', e);
    }

    // Fallback to Google
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyC6sIGZh3hpT002HR_qoDbHgbXyq46AbrI";
    try {
      const resp = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${location.lat},${location.lng}&key=${apiKey}`
      );
      if (resp.ok) {
        const data = await resp.json();
        if (data.status === 'OK' && data.results && data.results.length > 0) {
          return data.results[0].formatted_address;
        }
      }
    } catch (e) {}
    return `${location.lat?.toFixed?.(6) ?? ''}, ${location.lng?.toFixed?.(6) ?? ''}`;
  };

  const applyDriverRideDetails = (data) => {
    if (!data) return;
    setDriverId(data.driver_id ? String(data.driver_id) : '');
    setDriverPhone(data.driver_phone || data.driverPhone || '');
    setDriverName(data.driver_name || data.driverName || '');
    setRideId(data.ride_id ? String(data.ride_id) : '');
    setRideName(data.ride_name || data.rideName || data.vehicle_name || data.vehicle_type || '');
  };

  // Fetch tracking info using /fetch-tracking-location?token=...
  useEffect(() => {
    const token = getTokenFromUrl();
    if (!token) {
      setMapError('Tracking link is invalid or missing token.');
      return;
    }

    const fetchTracking = async () => {
      try {
        const apiBaseUrl = getApiBaseUrl();
        // URL encode the token to handle special characters
        const encodedToken = encodeURIComponent(token);
        const url = `${apiBaseUrl}/fetch-tracking-location?token=${encodedToken}`;
        
        const res = await fetch(url, {
          method: 'GET',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          mode: 'cors',
          credentials: 'omit'
        });
        if (!res.ok) throw new Error(`Failed to fetch tracking info: ${res.status} ${res.statusText}`);
        const data = await res.json();

        if (data.message === "ride tracking ended") {
          setTrackingEnded(true);
          setRoutePath([]); // Clear route path
          return;
        }

        // Required shape:
        // {
        //   "driver_id": 105,
        //   "driver_current_location": { "latitude": .., "longitude": .. },
        //   "pickup_location": { "latitude": .., "longitude": .. },
        //   "drop_location": { ... }
        // }
        const pickupLoc = data.pickup_location
          ? { lat: parseFloat(data.pickup_location.latitude), lng: parseFloat(data.pickup_location.longitude) }
          : null;
        const dropLoc = data.drop_location
          ? { lat: parseFloat(data.drop_location.latitude), lng: parseFloat(data.drop_location.longitude) }
          : null;
        const vehicleLoc = data.driver_current_location
          ? { lat: parseFloat(data.driver_current_location.latitude), lng: parseFloat(data.driver_current_location.longitude) }
          : null;

        setPickupLocation(pickupLoc);
        setDropLocation(dropLoc);
        setVehiclePosition(vehicleLoc);
        applyDriverRideDetails(data);
        
        if (vehicleLoc) {
          console.log('Initial driver location set:', vehicleLoc);
        }

        // Lookup addresses
        if (pickupLoc) fetchAddress(pickupLoc).then(addr => setPickupAddress(addr));
        if (dropLoc) fetchAddress(dropLoc).then(addr => setDropAddress(addr));
      } catch (e) {
        // Check if it's a CORS error
        if (e.message?.includes('CORS') || e.message?.includes('Failed to fetch') || e.name === 'TypeError') {
          setMapError('CORS Error: The API server is missing CORS headers. Please contact the backend team to add Access-Control-Allow-Origin headers to the API response.');
        } else {
          setMapError('Failed to fetch ride tracking. Please check your tracking link.');
        }
        console.error('Error fetching tracking:', e);
        setTrackingEnded(true);
      }
    };

    fetchTracking();
  }, []);

  // Periodically refresh tracking every 2 minutes for live driver location updates
  useEffect(() => {
    if (trackingEnded) return;
    const token = getTokenFromUrl();
    if (!token) return;

    // Initial call after component mounts
    const fetchDriverLocation = async () => {
      try {
        const apiBaseUrl = getApiBaseUrl();
        // URL encode the token to handle special characters
        const encodedToken = encodeURIComponent(token);
        const url = `${apiBaseUrl}/fetch-tracking-location?token=${encodedToken}`;
        
        console.log('Fetching driver location update...', new Date().toLocaleTimeString());
        
        const res = await fetch(url, {
          method: 'GET',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          mode: 'cors',
          credentials: 'omit'
        });
        if (!res.ok) throw new Error(`Failed to fetch tracking info: ${res.status} ${res.statusText}`);
        const data = await res.json();

        if (data.message === "ride tracking ended") {
          setTrackingEnded(true);
          setRoutePath([]); // Clear route path
          console.log('Ride tracking ended');
          return;
        }

        // Update positions
        const pickupLoc = data.pickup_location
          ? { lat: parseFloat(data.pickup_location.latitude), lng: parseFloat(data.pickup_location.longitude) }
          : null;
        const dropLoc = data.drop_location
          ? { lat: parseFloat(data.drop_location.latitude), lng: parseFloat(data.drop_location.longitude) }
          : null;
        const vehicleLoc = data.driver_current_location
          ? { lat: parseFloat(data.driver_current_location.latitude), lng: parseFloat(data.driver_current_location.longitude) }
          : null;

        // Update vehicle position for live tracking
        if (vehicleLoc) {
          console.log('Driver location updated:', vehicleLoc);
          setVehiclePosition(vehicleLoc);
        }

        // Update other locations (pickup/drop usually don't change, but update if needed)
        setPickupLocation(pickupLoc);
        setDropLocation(dropLoc);
        applyDriverRideDetails(data);

        // Addresses if blank (do not spam API)
        if (pickupLoc && !pickupAddress) fetchAddress(pickupLoc).then(addr => setPickupAddress(addr));
        if (dropLoc && !dropAddress) fetchAddress(dropLoc).then(addr => setDropAddress(addr));
      } catch (e) {
        // Log error but don't show to user on every poll
        console.error('Error polling tracking:', e);
        // Only set tracking ended if it's a critical error (not CORS)
        if (!e.message?.includes('CORS') && !e.message?.includes('Failed to fetch')) {
          setTrackingEnded(true);
        }
      }
    };

    // Call immediately on mount
    fetchDriverLocation();

    // Then set up interval to call every 2 minutes (120000ms)
    const interval = setInterval(fetchDriverLocation, 120000); // 2 minutes = 120000 milliseconds

    console.log('Live tracking started - polling every 2 minutes');

    return () => {
      clearInterval(interval);
      console.log('Live tracking stopped');
    };
    // dependencies must include pickup/drop/veh for blank address checks
    // eslint-disable-next-line
  }, [pickupAddress, dropAddress, trackingEnded]);

  // Google Maps directions - recalc route when pickup/drop changes and maps loaded
  const initializeDirectionsService = useCallback((map) => {
    if (!map || !window.google || !window.google.maps) {
      console.log('Map or Google Maps not available');
      return;
    }
    try {
      // With @vis.gl/react-google-maps, the map instance should be the Google Maps map
      // Try different ways to access the actual map instance
      let googleMap = map;
      
      // Check if map has innerMap property (some wrappers use this)
      if (map.innerMap) {
        googleMap = map.innerMap;
      }
      // Check if map has map property
      else if (map.map) {
        googleMap = map.map;
      }
      // Check if it's already a google.maps.Map instance
      else if (!(map instanceof window.google.maps.Map)) {
        // If it's not a Google Maps instance, try to get it from the context
        console.warn('Map instance might not be a Google Maps instance');
      }
      
      // Initialize DirectionsService
      if (!directionsServiceRef.current) {
        directionsServiceRef.current = new window.google.maps.DirectionsService();
      }
      
      // Clean up existing renderer
      if (directionsRendererRef.current) {
        try {
          directionsRendererRef.current.setMap(null);
        } catch (e) {
          // Ignore cleanup errors
        }
        directionsRendererRef.current = null;
      }
      
      // Create new DirectionsRenderer with proper configuration
      directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
        map: googleMap,
        suppressMarkers: true, // We use our own markers
        preserveViewport: false,
        polylineOptions: {
          strokeColor: '#3B82F6', // Blue color
          strokeWeight: 5,
          strokeOpacity: 0.8,
          zIndex: 1
        }
      });
      
      setMapsLoaded(true);
      console.log('Directions service initialized successfully');
    } catch (error) {
      console.error('Error initializing directions service:', error);
      // Try to set mapsLoaded anyway after a delay
      setTimeout(() => setMapsLoaded(true), 500);
    }
  }, []);

  const MapController = () => {
    const map = useMap();
    useEffect(() => {
      if (map) {
        mapRef.current = map;
        // Wait a bit for map to be fully ready
        const timer = setTimeout(() => {
          initializeDirectionsService(map);
        }, 100);
        return () => clearTimeout(timer);
      }
      // eslint-disable-next-line
    }, [map, initializeDirectionsService]);
    return null;
  };

  // Calculate directions with proper error handling
  const calculateDirections = useCallback(() => {
    if (!pickupLocation || !dropLocation) {
      console.log('Missing pickup or drop location');
      return;
    }
    
    if (!directionsServiceRef.current || !directionsRendererRef.current || !window.google) {
      console.log('Directions service not ready');
      return;
    }
    
    const request = {
      origin: new window.google.maps.LatLng(pickupLocation.lat, pickupLocation.lng),
      destination: new window.google.maps.LatLng(dropLocation.lat, dropLocation.lng),
      travelMode: window.google.maps.TravelMode.DRIVING,
      optimizeWaypoints: false,
      avoidHighways: false,
      avoidTolls: false
    };

    console.log('Calculating directions from', pickupLocation, 'to', dropLocation);

    directionsServiceRef.current.route(request, (result, status) => {
      console.log('Directions status:', status);
      if (status === window.google.maps.DirectionsStatus.OK) {
        console.log('Directions calculated successfully');
        setDirections(result);
        
        // Extract route path for Polyline fallback
        const path = [];
        if (result.routes && result.routes[0]) {
          const route = result.routes[0];
          if (route.overview_path) {
            route.overview_path.forEach(point => {
              path.push({ lat: point.lat(), lng: point.lng() });
            });
          } else if (route.legs && route.legs.length > 0) {
            route.legs.forEach(leg => {
              if (leg.steps) {
                leg.steps.forEach(step => {
                  if (step.path) {
                    step.path.forEach(point => {
                      path.push({ lat: point.lat(), lng: point.lng() });
                    });
                  }
                });
              }
            });
          }
        }
        setRoutePath(path);
        console.log('Route path extracted:', path.length, 'points');
        
        // Set directions on renderer
        try {
          if (directionsRendererRef.current) {
            directionsRendererRef.current.setDirections(result);
            console.log('Directions rendered on map via DirectionsRenderer');
          }
        } catch (error) {
          console.error('Error setting directions on renderer:', error);
          console.log('Will use Polyline fallback instead');
        }
        
        // Adjust map bounds to show entire route
        if (mapRef.current) {
          try {
            const googleMap = mapRef.current.innerMap || mapRef.current;
            const bounds = new window.google.maps.LatLngBounds();
            
            // Add pickup and drop points
            bounds.extend(new window.google.maps.LatLng(pickupLocation.lat, pickupLocation.lng));
            bounds.extend(new window.google.maps.LatLng(dropLocation.lat, dropLocation.lng));
            
            // Add route path points
            if (result.routes && result.routes[0] && result.routes[0].overview_path) {
              result.routes[0].overview_path.forEach(point => {
                bounds.extend(point);
              });
            }
            
            // Fit bounds with padding
            if (googleMap && typeof googleMap.fitBounds === 'function') {
              googleMap.fitBounds(bounds, { padding: 50 });
            }
          } catch (error) {
            console.error('Error fitting bounds:', error);
          }
        }
        
        // Estimate duration
        if (result.routes && result.routes[0]?.legs?.[0]) {
          const durationInSeconds = result.routes[0].legs[0].duration.value;
          setEstimatedTime(Math.ceil(durationInSeconds / 60));
          console.log('Estimated time:', Math.ceil(durationInSeconds / 60), 'minutes');
        }
      } else {
        console.error('Directions request failed:', status);
        setRoutePath([]); // Clear route path on error
        if (status === window.google.maps.DirectionsStatus.ZERO_RESULTS) {
          console.error('No route found between pickup and drop locations');
        } else if (status === window.google.maps.DirectionsStatus.REQUEST_DENIED) {
          console.error('Directions request denied - check API key permissions');
        }
      }
    });
  }, [pickupLocation, dropLocation]);

  // Recalculate directions when locations or map state changes
  useEffect(() => {
    if (pickupLocation && dropLocation && mapsLoaded && directionsServiceRef.current && directionsRendererRef.current) {
      // Small delay to ensure map is ready
      const timer = setTimeout(() => {
        calculateDirections();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [pickupLocation, dropLocation, mapsLoaded, calculateDirections]);

  // Manual polyline rendering as fallback/primary method
  useEffect(() => {
    if (!mapRef.current || !window.google || !window.google.maps || routePath.length === 0) {
      // Clean up existing polyline
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
      return;
    }

    try {
      const googleMap = mapRef.current.innerMap || mapRef.current.map || mapRef.current;
      
      // Clean up existing polyline
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
      }

      // Create new polyline
      const path = routePath.map(point => 
        new window.google.maps.LatLng(point.lat, point.lng)
      );

      polylineRef.current = new window.google.maps.Polyline({
        path: path,
        strokeColor: '#3B82F6', // Blue color
        strokeWeight: 5,
        strokeOpacity: 0.8,
        zIndex: 1,
        map: googleMap
      });

      console.log('Polyline rendered manually with', path.length, 'points');
    } catch (error) {
      console.error('Error rendering polyline:', error);
    }

    // Cleanup
    return () => {
      if (polylineRef.current) {
        try {
          polylineRef.current.setMap(null);
        } catch (e) {
          // Ignore cleanup errors
        }
        polylineRef.current = null;
      }
    };
  }, [routePath, mapsLoaded]);

  // Zoom and camera change handlers
  const handleCameraChange = useCallback((ev) => {
    if (ev?.detail) setZoom(ev.detail.zoom);
  }, []);
  const handleZoomIn = useCallback(() => {
    if (mapRef.current) {
      try {
        const curr = mapRef.current.getZoom();
        mapRef.current.setZoom(curr + 1);
        setZoom(curr + 1);
      } catch (e) {}
    }
  }, []);
  const handleZoomOut = useCallback(() => {
    if (mapRef.current) {
      try {
        const curr = mapRef.current.getZoom();
        mapRef.current.setZoom(Math.max(curr - 1, 1));
        setZoom(Math.max(curr - 1, 1));
      } catch (e) {}
    }
  }, []);

  useEffect(() => {
    const handleScriptError = (event) => {
      if (event.message?.includes('ExpiredKeyMapError') || event.message?.includes('InvalidKeyMapError')) {
        setMapError('Google Maps API key is expired or invalid. Please update the API key in your .env file.');
      }
    };
    const handleAuthFailure = () => {
      setMapError('Google Maps API key authentication failed. Please check your API key in the .env file.');
    };
    window.addEventListener('error', handleScriptError);
    window.addEventListener('gm_authFailure', handleAuthFailure);
    return () => {
      window.removeEventListener('error', handleScriptError);
      window.removeEventListener('gm_authFailure', handleAuthFailure);
    };
  }, []);

  const handleCallSupport = () => {
    window.location.href = 'tel:+919955210717';
  };

  const handleCallDriver = () => {
    if (!driverPhone) return;
    const phone = String(driverPhone).replace(/\s+/g, '');
    window.location.href = `tel:${phone}`;
  };

  const formatDriverPhone = (phone) => {
    if (!phone) return '';
    const digits = String(phone).replace(/[^\d+]/g, '');
    return digits;
  };

  // Calculate distance between two points in km
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };
  const distance = pickupLocation && dropLocation ?
    calculateDistance(
      pickupLocation.lat,
      pickupLocation.lng,
      dropLocation.lat,
      dropLocation.lng
    ).toFixed(1) : '0';

  // Estimate arrival time
  const estimatedArrival = new Date(Date.now() + (estimatedTime ? estimatedTime * 60 * 1000 : 0));
  // eslint-disable-next-line no-unused-vars
  const formattedArrival = estimatedTime ? estimatedArrival.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

  const displayRideName = rideName || (rideId ? `Ride #${rideId}` : 'Ride tracking');
  const displayDriverName = driverName || (driverId ? `Driver #${driverId}` : 'Your driver');

  // Center for default map if nothing
  const defaultCenter = pickupLocation || { lat: 19.8213723, lng: 75.2900127 };

  // Render
  return (
    <>
      <div className="flex flex-col h-screen w-full overflow-hidden">
        {/* 2-minute tracking notification banner */}
        {!trackingEnded && (
          <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 text-center z-50">
            <p className="text-sm text-blue-800 font-medium">
              📍 Location updates every 2 minutes
            </p>
          </div>
        )}
        
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden relative">
          {/* Sidebar - Mobile: overlay/top, Desktop: left side */}
          <div className="relative w-full md:w-96 md:max-w-md bg-white overflow-y-auto md:overflow-hidden md:h-full z-30 md:z-auto shadow-lg md:shadow-none">
            <div className="sticky md:relative top-0 bg-white z-10">
              <div className="absolute top-4 right-4 md:right-10 z-10">
                <img src={Buttons} alt="Google Play" className="w-20 h-20 md:w-24 md:h-24" />
              </div>
              <div className="bg-white rounded-b-xl shadow-md md:shadow-none mt-2 md:mt-0">
                <div className="p-4 pt-6 md:pt-4">
                  {!trackingEnded ? (
                    <>
                    <p className="text-xs font-semibold uppercase tracking-wide text-brand-gold mb-1">{displayRideName}</p>
                    <h1 className="text-lg md:text-xl font-bold text-brand-navy">Ride is Ongoing!</h1>
                    <p className="text-sm md:text-base text-gray-600">Estimated Drop Time: {estimatedTime || Math.ceil(distance * 3)} mins</p>
                    </>
                  ) : (
                    <h1 className="text-lg md:text-xl font-bold text-red-600">Ride tracking ended</h1>
                  )}
                  <div className="mt-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="mt-1 flex-shrink-0">
                        <div className="w-3 h-3 bg-green-500 rounded-full border-2 border-white ring-2 ring-green-500"></div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-green-500 font-medium">Pickup</p>
                        <p className="text-xs md:text-sm break-words">{pickupAddress || (pickupLocation ? `${pickupLocation.lat}, ${pickupLocation.lng}` : 'N/A')}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="mt-1 flex-shrink-0">
                        <div className="w-3 h-3 bg-red-500 rounded-full border-2 border-white ring-2 ring-red-500"></div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-red-500 font-medium">Drop</p>
                        <p className="text-xs md:text-sm break-words">{dropAddress || (dropLocation ? `${dropLocation.lat}, ${dropLocation.lng}` : 'N/A')}</p>
                      </div>
                    </div>
                  </div>
                  {/* Driver info */}
                  {!trackingEnded && (
                  <div className="mt-4 flex items-center gap-3">
                    <div className="relative w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden flex-shrink-0">
                      <img src={men} alt="Driver" className="object-cover w-full h-full" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm md:text-base font-medium truncate text-brand-navy">{displayDriverName}</p>
                      {driverPhone && (
                        <a href={`tel:${formatDriverPhone(driverPhone)}`} className="text-xs md:text-sm text-brand-navy/80 hover:text-brand-gold">
                          {formatDriverPhone(driverPhone)}
                        </a>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-gray-500">Driver ID: {driverId || 'N/A'}</p>
                    </div>
                  </div>
                  )}
                </div>
                {!trackingEnded && (
                <div className="px-4 pb-4 space-y-2">
                  <button
                    className="w-full bg-brand-gold text-brand-navy font-semibold rounded-md py-2.5 flex items-center justify-center gap-2 text-sm md:text-base hover:bg-brand-gold-dark disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleCallDriver}
                    disabled={!driverPhone}
                  >
                    <Phone className="w-4 h-4" />
                    <span>{driverPhone ? 'Call Driver' : 'Driver number unavailable'}</span>
                  </button>
                  <button className="w-full border border-gray-300 rounded-md py-2 flex items-center justify-center gap-2 text-gray-700 text-sm md:text-base" onClick={handleCallSupport}>
                    <Phone className="w-4 h-4" />
                    <span>Call Customer Support</span>
                  </button>
                </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Map - Full width on mobile, flex-1 on desktop */}
          <div className="flex-1 relative w-full h-full min-h-[400px] md:min-h-0">
            {mapError && (
              <div className="absolute inset-0 bg-white bg-opacity-95 z-30 flex items-center justify-center p-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
                  <h3 className="text-red-800 font-bold text-lg mb-2">Map Error</h3>
                  <p className="text-red-700 mb-4 text-sm">{mapError}</p>
                  <p className="text-xs text-red-600">
                    To fix this: Update <code className="bg-red-100 px-1 rounded">REACT_APP_GOOGLE_MAPS_API_KEY</code> in your <code className="bg-red-100 px-1 rounded">.env</code> file with a valid API key.
                  </p>
                  <button
                    onClick={() => setMapError(null)}
                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}
            <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyC6sIGZh3hpT002HR_qoDbHgbXyq46AbrI"}>
              <Map
                defaultZoom={14}
                defaultCenter={defaultCenter}
                onCameraChanged={handleCameraChange}
                style={mapContainerStyle}
                gestureHandling={'greedy'}
                disableDefaultUI={true}
                mapId="DEMO_MAP_ID"
              >
                <MapController />

                {pickupLocation && (
                  <AdvancedMarker position={pickupLocation}>
                    <Pin background={"#22C55E"} borderColor={"#16A34A"} glyphColor={"#FFFFFF"} />
                  </AdvancedMarker>
                )}

                {dropLocation && (
                  <AdvancedMarker position={dropLocation}>
                    <Pin background={"#EF4444"} borderColor={"#DC2626"} glyphColor={"#FFFFFF"} />
                  </AdvancedMarker>
                )}

                {vehiclePosition && !trackingEnded && (
                  <AdvancedMarker position={vehiclePosition} title="Vehicle">
                    <div className="p-2 bg-white rounded-full shadow-lg">
                      <img src={TrackIcon} alt="Vehicle" width="34" height="22" />
                    </div>
                  </AdvancedMarker>
                )}
              </Map>
            </APIProvider>
            {/* Zoom Controls */}
            {!trackingEnded && (
            <div className="absolute bottom-4 md:bottom-8 right-4 md:right-8 flex flex-col gap-2 z-20">
              <button 
                onClick={handleZoomIn}
                className="bg-white p-2 md:p-3 rounded-full shadow-lg hover:bg-gray-100 transition-colors"
                aria-label="Zoom in"
              >
                <ZoomIn size={18} className="md:w-5 md:h-5" />
              </button>
              <button 
                onClick={handleZoomOut}
                className="bg-white p-2 md:p-3 rounded-full shadow-lg hover:bg-gray-100 transition-colors"
                aria-label="Zoom out"
              >
                <ZoomOut size={18} className="md:w-5 md:h-5" />
              </button>
            </div>
            )}
            {/* Tracking status */}
            {(!trackingEnded && vehiclePosition) && (
              <div className="absolute top-2 md:top-4 left-2 md:left-4 bg-white p-2 rounded-lg shadow-md z-20">
                <div className="flex items-center">
                  <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-green-500 animate-pulse mr-2"></div>
                  <span className="text-xs md:text-sm font-medium">Tracking driver in real-time</span>
                </div>
              </div>
            )}
            {trackingEnded && (
              <div className="absolute inset-0 bg-white bg-opacity-85 z-40 flex items-center justify-center flex-col p-4">
                <h1 className="text-xl md:text-2xl font-bold text-red-600 mb-2 text-center">Ride tracking ended</h1>
                <p className="text-sm md:text-base text-gray-700 text-center">This trip is no longer being tracked.</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <footer className="bg-gray-100 py-2 md:py-3 px-4 md:px-6 text-center text-gray-500 text-xs md:text-sm">
        © 2025 EG ToGo • All Rights Reserved
      </footer>
    </>
  );
};

export default RideTrackingScreen;